/**
 * CLDST Fast ATC v3 — collection + PDP add-to-cart speed patches
 *
 * v3.1 (covers BOTH virtual + real collections):
 *  • Intercept .custom_atc (Boost virtual collections like /collections/glowie-turtle)
 *  • Intercept .lander-card-form (server-rendered collections like /collections/shop-all)
 *  • Catch-all on any product-card-scoped form posting to /cart/add
 *  • Explicitly EXCLUDE the PDP form (.product-purchase-form / #product_form)
 *    so PDP add-to-cart keeps its existing flow unchanged.
 *
 * v3 changes vs v2:
 *  • De-duplicate /cart.js polls. After /cart/add.js returns, ~6 scripts
 *    (drop-collections, shipping-progress, curated-collections, phase1,
 *    phase2, theme.js) all independently fetch /cart.js. They fire
 *    within ~50ms of each other. Cache the latest cart response for
 *    1.2s so they all share it instead of making 6 parallel requests.
 *    This is the single biggest win for cart-drawer-open perceived
 *    speed because Monster Cart's drawer waits behind those requests.
 *
 *  • Never fall back to form.submit() on AJAX failure. The previous
 *    behaviour — calling `if (form) form.submit()` in the .catch —
 *    caused a full page navigation to /cart any time the AJAX
 *    failed (Cloudflare bot challenge, network blip, 5xx). That's
 *    why some users would see the cart page open instead of the
 *    drawer. Now we retry once silently, then show a non-blocking
 *    toast.
 *
 *  • Run the intercept regardless of `theme.settings.cart_type`. The
 *    old guard `if (theme.settings.cart_type !== 'drawer') return`
 *    let the form submit through whenever theme settings weren't
 *    fully loaded. Now we always intercept — there is no scenario
 *    where letting the form do a default POST /cart/add helps.
 *
 *  • Preconnect to the Monster Cart CDN so the renderer is warm.
 *
 *  • Single /cart.js refresh after the add, not zero. We force one
 *    fetch and prime the cache from the response. Subsequent fetches
 *    from any other listener are served from cache.
 */
(function cldstFastATC() {
  'use strict';

  /* ─── 0. WARMUP ────────────────────────────────────────────────────── */
  function warmCart() {
    var origins = [
      'https://cdn.shopify.com'
    ];
    origins.forEach(function (origin) {
      if (document.querySelector('link[rel="preconnect"][href="' + origin + '"]')) return;
      var link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', warmCart);
  } else {
    warmCart();
  }

  /* ─── 1. /cart.js RESPONSE CACHE ───────────────────────────────────── */
  var cartCache = { data: null, ts: 0 };
  var CACHE_MS = 1200;
  // Bumped on every cart mutation: a /cart.js read that started before it must not repopulate the cache.
  var cartGen = 0;
  var MUT_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function invalidateCart() { cartCache.ts = 0; cartGen++; cartInflight = null; }
  // The one GET /cart.js currently in flight (Request 314), and the cart generation it belongs to.
  var cartInflight = null, cartInflightGen = 0;

  function setCartCache(data) {
    if (!data || typeof data !== 'object') return;
    cartCache = { data: data, ts: Date.now() };
  }
  function isCacheFresh() {
    return cartCache.data && (Date.now() - cartCache.ts) < CACHE_MS;
  }
  function isCartReadUrl(url, method) {
    if (!url || typeof url !== 'string') return false;
    if (method && method.toUpperCase() !== 'GET') return false;
    return /\/cart\.(js|json)(\?|$)/.test(url);
  }

  // Patch fetch
  var origFetch = window.fetch && window.fetch.bind(window);
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = (init && init.method) || (typeof input === 'object' && input && input.method) || 'GET';

      if (MUT_RE.test(url) && String(method).toUpperCase() !== 'GET') {
        invalidateCart();
        var mp = origFetch(input, init);
        mp.then(invalidateCart, invalidateCart);
        return mp;
      }

      if (isCartReadUrl(url, method) && isCacheFresh()) {
        var body = JSON.stringify(cartCache.data);
        var res = new Response(body, {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        });
        return Promise.resolve(res);
      }

      if (isCartReadUrl(url, method)) {
        // Request 314: one GET /cart.js in flight at a time. Four scripts read the cart at
        // page load within the same tick (the slide cart, phase2, the sticky bar, ...), and
        // the 1.2s cache above only helps once the first response has landed. Share the
        // in-flight request instead: every caller gets its own clone of the one response.
        if (cartInflight && cartInflightGen === cartGen) {
          return cartInflight.then(function (res) { return res.clone(); });
        }
        var g = cartGen;
        cartInflightGen = g;
        cartInflight = origFetch(input, init).then(function (res) {
          if (cartInflight && cartInflightGen === g) cartInflight = null;
          if (res && res.ok) {
            try {
              res.clone().json().then(function (d) { if (g === cartGen) setCartCache(d); }).catch(function () {});
            } catch (e) {}
          }
          return res;
        }, function (err) {
          if (cartInflightGen === g) cartInflight = null;
          throw err;
        });
        return cartInflight.then(function (res) { return res.clone(); });
      }

      return origFetch(input, init);
    };
  }

  // Patch XHR (jQuery and older scripts often use it)
  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cldstUrl = url;
    this.__cldstMethod = method;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var self = this;
    var url = this.__cldstUrl || '';
    var method = (this.__cldstMethod || 'GET');

    if (MUT_RE.test(url) && String(method).toUpperCase() !== 'GET') {
      invalidateCart();
      this.addEventListener('loadend', invalidateCart);
      return origSend.apply(this, arguments);
    }

    if (isCartReadUrl(url, method)) {
      var g = cartGen;
      this.addEventListener('load', function () {
        if (g === cartGen && self.status >= 200 && self.status < 300) {
          try { setCartCache(JSON.parse(self.responseText)); } catch (e) {}
        }
      });
    }

    return origSend.apply(this, arguments);
  };

  /* ─── 2. ATC FORM INTERCEPT (collection cards) ─────────────────────── */
  var CART_ADD_URL = '/cart/add.js';

  function setBusy(btn, busy) {
    if (!btn) return;
    var sp = btn.querySelector('.spinner');
    if (sp) sp.style.display = busy ? 'block' : '';
    btn.disabled = !!busy;
    if (busy) btn.dataset.cldstBusy = '1';
    else delete btn.dataset.cldstBusy;
  }

  function showErrorToast(msg) {
    try {
      var t = document.createElement('div');
      t.textContent = msg;
      t.setAttribute('role', 'alert');
      t.style.cssText =
        'position:fixed;top:20px;left:50%;transform:translateX(-50%);' +
        'background:#1a1a1a;color:#fff;padding:12px 20px;border-radius:6px;' +
        'z-index:99999;font-size:14px;font-weight:500;' +
        'box-shadow:0 4px 14px rgba(0,0,0,0.25);max-width:90vw';
      document.body.appendChild(t);
      setTimeout(function () { t.remove(); }, 4000);
    } catch (e) {}
  }

  function addToCartFast(variantId, btn, form, attempt) {
    attempt = attempt || 1;
    setBusy(btn, true);
    invalidateCart();

    var body = new URLSearchParams();
    body.append('id', variantId);
    body.append('quantity', '1');
    body.append('sections', 'cart-drawer');

    return (origFetch || fetch)(CART_ADD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: body.toString(),
      credentials: 'same-origin'
    })
      .then(function (r) {
        invalidateCart();
        if (!r.ok) {
          // Treat as a real error so we retry / toast — never fall back
          // to letting the browser submit the form (that navigates).
          throw new Error('add_failed_' + r.status);
        }
        return r.json();
      })
      .then(function (data) {
        setBusy(btn, false);
        // Open the AOV drawer (canonical helper: snippets/coldest-atc-drawer.liquid).
        // Without this, a card add only moved the badge — no drawer.
        if (typeof window.coldestOpenCartDrawer === 'function') {
          try { window.coldestOpenCartDrawer(); } catch (e) {}
        }

        // Single /cart.js refresh — primes the cache for all the other
        // scripts that will fire their own /cart.js fetch.
        (origFetch || fetch)('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            setCartCache(cart);
            document.documentElement.dispatchEvent(
              new CustomEvent('theme:cartchanged', {
                bubbles: true,
                cancelable: false,
                detail: { cart: cart }
              })
            );
            document.dispatchEvent(
              new CustomEvent('cart:updated', {
                bubbles: true,
                cancelable: false,
                detail: { cart: cart }
              })
            );
          })
          .catch(function () {
            // Even if the refresh fails, dispatch the event so apps
            // can react (they'll fall back to their own fetch).
            document.documentElement.dispatchEvent(
              new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false })
            );
          });

        // Theme's built-in handler still fires its mini-cart overlay
        if (
          data.sections &&
          data.sections['cart-drawer'] &&
          typeof window.theme !== 'undefined' &&
          window.theme.addedToCartHandler
        ) {
          try { window.theme.addedToCartHandler.call(form, data); } catch (e) {}
        }
      })
      .catch(function (err) {
        setBusy(btn, false);
        // Retry once on transient failure
        if (attempt < 2) {
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(addToCartFast(variantId, btn, form, attempt + 1));
            }, 350);
          });
        }
        showErrorToast('Could not add to cart. Please try again.');
      });
  }

  // NOTE: the PDP product form (.product-purchase-form) is intentionally NOT
  // intercepted here. It keeps the theme/app's native add-to-cart flow. (An
  // earlier attempt to AJAX-intercept it fought the live cart app and left the
  // button spinning, so it was reverted.) A capture-phase preventDefault test
  // (20oz only) ALSO broke add-to-cart, so even blocking just the navigation is
  // not safe with this theme/app combo — leave the PDP form completely alone.

  // Match every collection-card ATC form on the site:
  //   .custom_atc        — Boost-rendered virtual collections (.product-block)
  //   .lander-card-form  — server-rendered "lander" collections (shop-all etc.)
  // Fallback: any form whose action ends with /cart/add and is inside a
  // product card (not the PDP main form). The PDP form is .product-purchase-form
  // which we explicitly exclude so PDP add-to-cart keeps using its own flow.
  function isCollectionAtcForm(form) {
    if (!form || !form.classList) return false;
    if (form.classList.contains('custom_atc')) return true;
    if (form.classList.contains('lander-card-form')) return true;
    // Catch-all: any /cart/add form on a collection page that isn't the PDP
    var action = form.getAttribute('action') || '';
    if (!/\/cart\/add(?:\?|$|\/)/.test(action)) return false;
    if (form.classList.contains('product-purchase-form')) return false;
    if (form.id === 'product_form' || form.matches('[id^="product-form"]')) return false;
    // Only intercept forms inside product cards (not random page-level forms)
    return !!form.closest('.product-block, .lander-card, [class*="product-card"], [class*="product-item"], [class*="product-grid-item"]');
  }

  document.addEventListener(
    'submit',
    function (evt) {
      var form = evt.target;
      if (!isCollectionAtcForm(form)) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();

      var idInput = form.querySelector('input[name="id"]');
      if (!idInput || !idInput.value) return;
      var btn = form.querySelector('button[type="submit"]');
      if (btn && btn.dataset.cldstBusy === '1') return; // debounce
      addToCartFast(idInput.value, btn, form);
    },
    true
  );

  /* ─── 3. BOOST SD BUTTONS: instant loading state ──────────────────── */
  var BOOST_LOADING = 'cldst-boost-loading';
  document.addEventListener(
    'click',
    function (evt) {
      if (!evt.target.closest) return;
      var btn = evt.target.closest(
        '.boost-sd__button--select-option, .boost-sd__button[aria-label*="cart"], .boost-sd__button[aria-label*="Cart"]'
      );
      if (!btn) return;
      if (btn.classList.contains(BOOST_LOADING)) return;
      btn.classList.add(BOOST_LOADING);
      setTimeout(function () { btn.classList.remove(BOOST_LOADING); }, 5000);
    },
    true
  );
  document.documentElement.addEventListener('theme:cartchanged', function () {
    document.querySelectorAll('.' + BOOST_LOADING).forEach(function (el) {
      el.classList.remove(BOOST_LOADING);
    });
  });

  /* ─── 4. QUICKBUY PREFETCH ─────────────────────────────────────────── */
  document.addEventListener('mouseover', function (evt) {
    if (!evt.target.closest) return;
    var toggle = evt.target.closest('.quickbuy-toggle');
    if (!toggle) return;
    var href = toggle.getAttribute('href');
    if (!href || toggle.dataset.cldstPrefetched) return;
    toggle.dataset.cldstPrefetched = '1';
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = href;
    document.head.appendChild(link);
  });

  /* ─── 5. PDP ATC: instant loading state ────────────────────────────── */
  var BTN_SEL = '#tfc_product_btn';
  var LOADING = 'cldst-atc-loading';
  document.addEventListener(
    'click',
    function (evt) {
      if (!evt.target.closest) return;
      var btn = evt.target.closest(BTN_SEL);
      if (!btn) return;
      var form = btn.closest('form.product-purchase-form');
      if (!form) return;
      if (btn.classList.contains(LOADING)) return;
      btn.classList.add(LOADING);
      setTimeout(function () { btn.classList.remove(LOADING); }, 4000);
    },
    true
  );
  document.documentElement.addEventListener('theme:cartchanged', function () {
    var btn = document.querySelector(BTN_SEL);
    if (btn) btn.classList.remove(LOADING);
  });
})();
